import { createElement, PureComponent } from 'react';
import Observer from 'react-intersection-observer';
import invariant from 'invariant';
var isMonitoring = false;
var isScrolling = false;
var watchers = new Set();
function onScroll(e) {
  if (!isScrolling) {
    isScrolling = true;
    requestAnimationFrame(update);
  }
}
function update() {
  isScrolling = false;
  watchers.forEach(function (cb) {
    return cb();
  });
}
function start() {
  if (!isMonitoring) {
    window.addEventListener('scroll', onScroll);
    isMonitoring = true;
  }
}
function stop() {
  if (isMonitoring) {
    watchers.clear();
    window.removeEventListener('scroll', onScroll);
    isMonitoring = false;
  }
}
function watch(cb) {
  if (!isMonitoring) start();
  watchers.add(cb);
}
function unwatch(cb) {
  watchers.delete(cb);
  if (!watchers.size) stop();
}
var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};
var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
};
var inherits = function (subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }
  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
};
var objectWithoutProperties = function (obj, keys) {
  var target = {};
  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }
  return target;
};
var possibleConstructorReturn = function (self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }
  return call && (typeof call === "object" || typeof call === "function") ? call : self;
};

/**
 * Monitors scroll, and triggers the children function with updated props
 *
 <ScrollPercentage>
 {({inView, percentage}) => (
   <h1>{`${inView} {percentage}`}</h1>
 )}
 </ScrollPercentage>
 */
var ScrollPercentage = function (_React$PureComponent) {
  inherits(ScrollPercentage, _React$PureComponent);
  function ScrollPercentage() {
    var _temp, _this, _ret;
    classCallCheck(this, ScrollPercentage);
    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return _ret = (_temp = (_this = possibleConstructorReturn(this, _React$PureComponent.call.apply(_React$PureComponent, [this].concat(args))), _this), _this.state = {
      percentage: 0,
      inView: false
    }, _this.node = null, _this.handleInView = function (inView) {
      _this.setState({
        inView: inView
      });
    }, _this.handleNode = function (observer) {
      return _this.node = observer && observer.node;
    }, _this.handleScroll = function () {
      if (!_this.node) return;
      var threshold = _this.props.threshold;
      var bounds = _this.node.getBoundingClientRect();
      var percentage = ScrollPercentage.calculatePercentage(bounds, threshold);
      if (percentage !== _this.state.percentage) {
        _this.setState({
          percentage: percentage
        });
      }
    }, _this.renderInner = function (_ref) {
      var inView = _ref.inView,
        ref = _ref.ref;
      var _this$props = _this.props,
        children = _this$props.children,
        onChange = _this$props.onChange,
        threshold = _this$props.threshold,
        innerRef = _this$props.innerRef,
        tag = _this$props.tag,
        props = objectWithoutProperties(_this$props, ['children', 'onChange', 'threshold', 'innerRef', 'tag']);

      // Create a wrapping element

      return createElement(tag || 'div', _extends({
        ref: innerRef ? function (node) {
          innerRef(node);
          ref(node);
        } : ref
      }, props), typeof children === 'function' ? children({
        percentage: _this.state.percentage,
        inView: inView
      }) : children);
    }, _temp), possibleConstructorReturn(_this, _ret);
  }

  /**
   * Get the correct viewport height. If rendered inside an iframe, grab it from the parent
   */
  ScrollPercentage.viewportHeight = function viewportHeight() {
    return global.parent ? global.parent.innerHeight : global.innerHeight || 0;
  };
  ScrollPercentage.calculatePercentage = function calculatePercentage(bounds) {
    var threshold = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    var vh = ScrollPercentage.viewportHeight();
    var offsetTop = threshold * vh * 0.25;
    var offsetBottom = threshold * vh * 0.25;
    return 1 - Math.max(0, Math.min(1, (bounds.bottom - offsetTop) / (vh + bounds.height - offsetBottom - offsetTop)));
  };
  ScrollPercentage.prototype.componentDidMount = function componentDidMount() {
    // Start by updating the scroll position, so it correctly reflects the elements start position
    this.handleScroll();
    if (process.env.NODE_ENV !== 'production') {
      invariant(this.node, 'react-scroll-percentage: No DOM node found. Make sure you forward "ref" to the root DOM element you want to observe, when using render prop.');
    }
  };
  ScrollPercentage.prototype.componentDidUpdate = function componentDidUpdate(prevProps, prevState) {
    if (this.props.onChange && (prevState.percentage !== this.state.percentage || prevState.inView !== this.state.inView)) {
      this.props.onChange(this.state.percentage, this.state.inView);
    }
    if (prevState.inView !== this.state.inView) {
      this.monitorScroll(this.state.inView);
    }
  };
  ScrollPercentage.prototype.componentWillUnmount = function componentWillUnmount() {
    this.monitorScroll(false);
  };
  ScrollPercentage.prototype.monitorScroll = function monitorScroll(enable) {
    if (enable) {
      watch(this.handleScroll);
    } else {
      unwatch(this.handleScroll);
    }
  };
  ScrollPercentage.prototype.render = function render() {
    return createElement(Observer, {
      onChange: this.handleInView,
      threshold: this.props.threshold,
      ref: this.handleNode
    }, this.renderInner);
  };
  return ScrollPercentage;
}(PureComponent);
ScrollPercentage.defaultProps = {
  tag: 'div',
  threshold: 0
};
export default ScrollPercentage;